/* eslint-disable react/jsx-no-bind */
import { useState } from 'react';

import { useTheme } from 'styled-components';

import { wellzTheme } from '@wellz-theme/mui';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useIntl } from 'react-intl';

import { useModals } from 'context';
import { useRandomColors } from 'hooks';
import { SCREEN_MIN_WIDTH } from 'hooks/useComplaintDevice/useComplaintDevice';
import { SpecialistChatUser } from 'services';

import { ChatList, ChatListEmptyState, ChatListItem, Modal } from 'components';

import { ChatListModalProps } from './types';

import * as S from './styles';

const ChatListModal: React.FC<ChatListModalProps> = ({
  isOpen,
  onClose,
  modalKey,
  props: { chatList, onFilterPatients, next, hasMore, onSelectConversation },
}) => {
  const { closeModal } = useModals();

  const intl = useIntl();
  const theme = useTheme() as any; // eslint-disable-line

  const title = intl.formatMessage({ id: 'CHAT.PAGE.PATIENTS_LIST.TITLE' });

  const [mode, setMode] = useState('open');

  const { colors } = useRandomColors(chatList.length);

  const isMobile = window.innerWidth <= SCREEN_MIN_WIDTH;

  const handleCloseModal = () => {
    if (onClose) {
      onClose();
    }
    setMode('close');

    setTimeout(() => {
      closeModal(modalKey);
    }, 500);
  };

  const renderListItems = (
    { user, chat }: SpecialistChatUser,
    avatarColor?: string,
  ) => (
    <ChatListItem
      key={user.id}
      avatarColor={avatarColor}
      fullName={user.full_name}
      endUserCognitoId={user.cognito_id}
      closeNewMessageModal={handleCloseModal}
      onSelect={onSelectConversation}
      hasScheduledMessage={chat.has_scheduled_messages}
      isCritical={user.is_critical}
    />
  );

  return (
    <Modal
      isOpen={isOpen}
      showCloseButton={false}
      closeModal={handleCloseModal}
      style={{
        overlay: {
          zIndex: 0,
          display: 'grid',
          alignItems: 'center',
          justifyItems: 'left',
          backgroundColor: 'transparent',
          width: isMobile ? '100vw' : `${S.patientsModalWidth}px`,
        },
        content: {
          border: 'none',
          inset: 'unset',
          height: `100vh`,
          position: 'relative',
          transition: 'width 2s',
          backgroundColor: 'transparent',
          width: isMobile ? '100vw' : `${S.patientsModalWidth}px`,
          margin: isMobile
            ? `${wellzTheme.spacing(8.5)} ${wellzTheme.spacing(0)}`
            : `${wellzTheme.spacing(0)}`,
          padding: `${theme.yoga.spacing.zero}`,
        },
      }}
    >
      <S.Container data-testid="patientsContainer">
        <div className={mode}>
          <ChatList
            title={title}
            showArrowButton
            hideNewMessageButton
            hideTipsButton
            data-testid="chatList"
            onFilterPatients={onFilterPatients}
            onClickArrowButton={handleCloseModal}
            id="scrollableModal"
          >
            {chatList.length ? (
              <InfiniteScroll
                next={next}
                hasMore={hasMore}
                loader={undefined}
                dataLength={chatList.length}
                scrollThreshold="20px"
                scrollableTarget="scrollableModal"
              >
                <div>
                  {chatList.map((patient, index) =>
                    renderListItems(patient, colors[index]),
                  )}
                </div>
              </InfiniteScroll>
            ) : (
              <ChatListEmptyState />
            )}
          </ChatList>
        </div>
      </S.Container>
    </Modal>
  );
};

export default ChatListModal;
